import { useEffect, useState } from "react";
import { Col, Container, Row, Card, Badge } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { PatientFileVersion } from "../../../../amplify/api-gateway/query";
import { PatientVisit } from "../../../../amplify/api-gateway/search-patients";
import { EVALUATE_CAPACITY_OUTPUT } from "../../../../amplify/api-gateway/write";
import { UserRole } from "../../../../utils/user";
import { useGlobalOptions } from "../../../common/GlobalOptionsContext";
import { dashboardLLMCache } from "../cache";

export interface Evaluation {
  evaluation_summary: string;
  ddx_evaluation: DdxEvaluation;
  risk_evaluation: RiskEvaluation;
  patient_visit_summary_evaluation: PatientVisitSummaryEvaluation;
  charting_evaluation: ChartingEvaluation;
  overall_assessment: OverallAssessment;
  lessons_learned: Lesson[];
}

export interface DdxEvaluation {
  true_diagnosis_documented: string;
  true_diagnosis_in_capacity_ddx: boolean;
  capacity_ddx_rank: number;
  true_diagnosis_knowable_at_dispo: boolean;
  true_diagnosis_knowable_justification: string;
  high_risk_diagnoses_appropriate: boolean;
  high_risk_assessment: string;
  ddx_quality_rating: "poor" | "fair" | "good" | "excellent";
  ddx_improvement_recommendation: string;
}

export interface RiskEvaluation {
  admit_prediction_vs_actual: RiskPrediction;
  icu_bounceup_24h_vs_actual: RiskPrediction;
  risk_overall_assessment: string;
}

export interface RiskPrediction {
  capacity_predicted_probability: number;
  capacity_risk_level: "Low" | "Medium" | "High";
  actual_outcome: string;
  prediction_accurate: boolean;
  missed_features: string[];
  improvement_recommendation: string;
}

export interface PatientVisitSummaryEvaluation {
  patient_summary_accurate: "accurate" | "partial" | "inaccurate";
  patient_summary_assessment: string;
  visit_presentation_accurate: boolean;
  visit_presentation_assessment: string;
  summary_improvement_recommendation: string;
}

export interface ChartingEvaluation {
  workup_summary_accurate: boolean;
  workup_assessment: string;
  management_summary_accurate: boolean;
  management_assessment: string;
  disposition_reasoning_aligned: boolean;
  disposition_assessment: string;
  charting_improvement_recommendation: string;
}

export interface OverallAssessment {
  most_significant_success: string;
  most_significant_miss: string;
  one_thing_to_change: string;
  overall_capacity_performance_rating: "poor" | "fair" | "good" | "excellent";
  overall_justification: string;
}

export interface Lesson {
  category: string;
  lesson: string;
  generalizability: "low" | "medium" | "high";
}

const mockEvaluation: Evaluation = {
    "evaluation_summary": "Capacity demonstrated excellent clinical reasoning by creating a safety-oriented differential diagnosis that prioritized life-threatening conditions while still including the eventual working diagnosis of heartburn. The system's summaries of the patient's history and workup were largely accurate and useful. However, Capacity's most significant miss was incorrectly predicting admission and, more critically, stating this incorrect prediction as a factual outcome in the charting summary. This highlights a key area for improvement in distinguishing between prediction and documentation.",
    "ddx_evaluation": {
        "true_diagnosis_documented": "Heart burn suspected (non-cardiac chest pain / GERD).",
        "true_diagnosis_in_capacity_ddx": true,
        "capacity_ddx_rank": 5,
        "true_diagnosis_knowable_at_dispo": true,
        "true_diagnosis_knowable_justification": "Yes, this was a clinical diagnosis made based on the atypical nature of the pain, stable troponins/EKGs, and symptomatic improvement after receiving Maalox and Pepcid in the ED.",
        "high_risk_diagnoses_appropriate": true,
        "high_risk_assessment": "Capacity correctly flagged Hypertensive Emergency, ACS, Aortic Dissection, and PE as high-risk, appropriately prioritizing the 'can't-miss' diagnoses for this presentation.",
        "ddx_quality_rating": "excellent",
        "ddx_improvement_recommendation": "The DDx was high quality. For further refinement, the model could improve its ingestion of nuanced findings from prior imaging reports. For example, the ground truth notes a prior CT showed 'no significant coronary artery calcifications,' whereas Capacity cited 'Coronary artery calcification' as evidence for ACS. Incorporating such modifiers ('significant', 'mild') could help fine-tune the likelihood scores for diagnoses like ACS."
    },
    "risk_evaluation": {
        "admit_prediction_vs_actual": {
            "capacity_predicted_probability": 0.6725,
            "capacity_risk_level": "High",
            "actual_outcome": "discharged",
            "prediction_accurate": false,
            "missed_features": [
                "Patient's positive response to non-cardiac therapy (Maalox, Pepcid)",
                "Goals-of-care discussion with conservator influencing decision to avoid aggressive workup",
                "Physician's clinical gestalt that chest pain was atypical for angina"
            ],
            "improvement_recommendation": "The model correctly identified admission drivers but failed to account for de-escalation factors. It could be improved by incorporating features that represent response to therapy or flagging cases where goals-of-care may heavily influence disposition."
        },
        "icu_bounceup_24h_vs_actual": {
            "capacity_predicted_probability": 0.0012,
            "capacity_risk_level": "Low",
            "actual_outcome": "no_bounceup",
            "prediction_accurate": true,
            "missed_features": [],
            "improvement_recommendation": "The prediction was accurate and well-justified based on the patient's stable vital signs and lack of end-organ failure. No improvement needed for this specific prediction."
        },
        "risk_overall_assessment": "Capacity's risk prediction was a mixed bag. It accurately identified the low risk of ICU escalation and the high long-term risks (revisit, mortality) based on the patient's profile. However, it failed on the most immediate prediction: admission vs. discharge, highlighting a vulnerability to nuanced clinical decision-making and goals-of-care factors that are not easily captured from structured data."
    },
    "patient_visit_summary_evaluation": {
        "patient_summary_accurate": "partial",
        "patient_summary_assessment": "The summary was mostly accurate but notably omitted the patient's history of HFpEF (diastolic heart failure), a clinically significant comorbidity. Interestingly, Capacity's DDx module did identify this condition, indicating an internal consistency gap.",
        "visit_presentation_accurate": true,
        "visit_presentation_assessment": "The summary of the current visit presentation was accurate and concise, correctly capturing the chief complaint of burning chest pain and severe hypertension.",
        "summary_improvement_recommendation": "Ensure consistency across modules. If a significant comorbidity like HFpEF is identified and used for the DDx, it must be included in the top-level patient summary to provide a complete clinical picture."
    },
    "charting_evaluation": {
        "workup_summary_accurate": true,
        "workup_assessment": "The workup summary was excellent, accurately noting stable troponins without dynamic change, which was the key takeaway for ruling out acute MI.",
        "management_summary_accurate": true,
        "management_assessment": "The management summary correctly identified the key interventions (antiulcer meds, nitroglycerin) and the patient's positive response (BP reduction).",
        "disposition_reasoning_aligned": false,
        "disposition_assessment": "The disposition summary was factually incorrect. It stated 'The patient was admitted as an inpatient,' whereas the patient was discharged home. This is a critical error.",
        "charting_improvement_recommendation": "The system must be changed to frame disposition as a recommendation or predicted plan (e.g., 'Admission to inpatient is recommended for...') rather than a statement of fact ('The patient was admitted...'). This prevents the creation of an inaccurate record if the clinical team's final decision differs from the AI's prediction."
    },
    "overall_assessment": {
        "most_significant_success": "Capacity's most significant success was generating a clinically sound and safe differential diagnosis that correctly prioritized life-threatening possibilities based on the initial presentation, while also including the eventual, less acute diagnosis.",
        "most_significant_miss": "The most significant miss was factually misstating the patient's disposition in the charting summary ('was admitted' instead of discharged), turning a potentially helpful tool into a source of misinformation.",
        "one_thing_to_change": "I would change the phrasing of the 'disposition' field in the Charting Summary from a declarative statement to a conditional recommendation. Instead of 'The patient was admitted...', it should say 'Admission is recommended for ongoing workup...'. This change would maintain the summary's utility as decision support without creating a false record, acknowledging that the AI provides input, but the human clinician makes the final call.",
        "overall_capacity_performance_rating": "good",
        "overall_justification": "The system earns a 'good' rating due to its excellent DDx, accurate risk stratification for ICU, and helpful summarization of workup and management. The performance was downgraded from 'excellent' because of the critical error in the charting summary's disposition and the missed admission prediction, which, while understandable, represents a key area for improvement in bridging the gap between data-driven prediction and human-centered clinical decisions."
    },
    "lessons_learned": [
        {
            "category": "Charting",
            "lesson": "AI-generated documentation must clearly delineate between summarizing historical facts and presenting predictions or recommendations. Stating a prediction as a concluded event is a significant safety and accuracy risk.",
            "generalizability": "high"
        },
        {
            "category": "Risk",
            "lesson": "Disposition models are inherently limited if they cannot account for real-time response to therapy and nuanced, human factors like goals-of-care discussions. This case shows that even with high admission probability based on initial data, these factors can rightfully lead to a discharge decision.",
            "generalizability": "high"
        }
    ]
};

export const EvaluationDashboard = (props: {
    user: any,
    ws: string,
    tenant: string,
    patientVisit?: PatientVisit,
    unreadFileVersionMap?: Record<string, PatientFileVersion>,
    setUnreadFileVersionMap?: (map: Record<string, PatientFileVersion>) => void,
}) => {
    const { getDerivedRole } = useGlobalOptions();
    let { internalCSN } = useParams();
    const patientVisit = props.patientVisit;

    const [loading, setLoading] = useState<boolean>(false);
    const [evaluation, setEvaluation] = useState<Evaluation>();

    useEffect(() => {
        if (patientVisit) {
            setLoading(true);
            dashboardLLMCache.get(EVALUATE_CAPACITY_OUTPUT, patientVisit, props.tenant)
            .then((data: any) => {
                if (data && data.response) {
                    setEvaluation(data.response);
                }
            }).finally(() => {
                setLoading(false);
            });
        }
    }, [patientVisit]);

    const isAdmin = getDerivedRole(props.user) === UserRole.ADMIN;

    const getRatingColor = (rating: string) => {
        switch (rating) {
            case "excellent": return "success";
            case "good": return "info";
            case "fair": return "warning";
            case "poor": return "danger";
            default: return "secondary";
        }
    };

    const getAccuracyBadge = (accurate: boolean | string) => {
        if (typeof accurate === "boolean") {
            return accurate ? 
                <Badge bg="success">✓ Accurate</Badge> : 
                <Badge bg="danger">✗ Inaccurate</Badge>;
        }
        switch (accurate) {
            case "accurate": return <Badge bg="success">Accurate</Badge>;
            case "partial": return <Badge bg="warning">Partially Accurate</Badge>;
            case "inaccurate": return <Badge bg="danger">Inaccurate</Badge>;
            default: return null;
        }
    };

    const getRiskLevelBadge = (level: string) => {
        const variant = level === "Low" ? "success" : level === "Medium" ? "warning" : "danger";
        return <Badge bg={variant}>{level} Risk</Badge>;
    };

    const getGeneralizabilityBadge = (level: string) => {
        const variant = level === "high" ? "success" : level === "medium" ? "warning" : "secondary";
        return <Badge bg={variant}>{level}</Badge>;
    };

    if (!isAdmin) {
        return <>Must be Admin to view</>
    }

    if (loading) {
        return <Container className="notes-dashboard"><div className="text-center p-5">Loading evaluation...</div></Container>;
    }

    if (!evaluation) {
        return <Container className="notes-dashboard"><div className="text-center p-5">No evaluation found</div></Container>;
    }

    return (
        <Container className="notes-dashboard">
            <Row className="mb-3">
                <Col>
                    <span className="gradient-text" style={{fontSize: '1.25rem'}}>Capacity Evaluation Dashboard</span>
                </Col>
            </Row>

            {/* Executive Summary */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header style={{backgroundColor: '#0d6efd', color: 'white'}}>
                            <h5 className="mb-0">Executive Summary</h5>
                        </Card.Header>
                        <Card.Body>
                            <p>{evaluation.evaluation_summary}</p>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Overall Assessment */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Overall Assessment</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>Performance Rating: </strong>
                                    <Badge bg={getRatingColor(evaluation.overall_assessment.overall_capacity_performance_rating)} style={{fontSize: '1rem'}}>
                                        {evaluation.overall_assessment.overall_capacity_performance_rating.toUpperCase()}
                                    </Badge>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col>
                                    <strong>Justification:</strong>
                                    <p className="mt-2">{evaluation.overall_assessment.overall_justification}</p>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Card bg="light" className="border-success">
                                        <Card.Body>
                                            <strong className="text-success">Most Significant Success:</strong>
                                            <p className="mt-2 mb-0">{evaluation.overall_assessment.most_significant_success}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={6}>
                                    <Card bg="light" className="border-danger">
                                        <Card.Body>
                                            <strong className="text-danger">Most Significant Miss:</strong>
                                            <p className="mt-2 mb-0">{evaluation.overall_assessment.most_significant_miss}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Card bg="light" className="border-start border-warning border-4">
                                        <Card.Body>
                                            <strong>One Thing to Change:</strong>
                                            <p className="mt-2 mb-0">{evaluation.overall_assessment.one_thing_to_change}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Differential Diagnosis Evaluation */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header style={{backgroundColor: '#d1e7dd'}}>
                            <h5 className="mb-0">Differential Diagnosis Evaluation</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>Quality Rating: </strong>
                                    <Badge bg={getRatingColor(evaluation.ddx_evaluation.ddx_quality_rating)} style={{fontSize: '1rem'}}>
                                        {evaluation.ddx_evaluation.ddx_quality_rating.toUpperCase()}
                                    </Badge>
                                </Col>
                                <Col md={6}>
                                    <strong>True Diagnosis in DDx: </strong>
                                    {getAccuracyBadge(evaluation.ddx_evaluation.true_diagnosis_in_capacity_ddx)}
                                    {evaluation.ddx_evaluation.true_diagnosis_in_capacity_ddx && (
                                        <span className="ms-2 text-muted">(Rank: #{evaluation.ddx_evaluation.capacity_ddx_rank})</span>
                                    )}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col>
                                    <strong>True Diagnosis:</strong>
                                    <p className="mt-1">{evaluation.ddx_evaluation.true_diagnosis_documented}</p>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col>
                                    <strong>Knowable at Disposition: </strong>
                                    {getAccuracyBadge(evaluation.ddx_evaluation.true_diagnosis_knowable_at_dispo)}
                                    <p className="mt-2">{evaluation.ddx_evaluation.true_diagnosis_knowable_justification}</p>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col>
                                    <strong>High-Risk Diagnoses Appropriate: </strong>
                                    {getAccuracyBadge(evaluation.ddx_evaluation.high_risk_diagnoses_appropriate)}
                                    <p className="mt-2">{evaluation.ddx_evaluation.high_risk_assessment}</p>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Card bg="light">
                                        <Card.Body>
                                            <strong>Improvement Recommendation:</strong>
                                            <p className="mt-2 mb-0">{evaluation.ddx_evaluation.ddx_improvement_recommendation}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Risk Evaluation */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header style={{backgroundColor: '#fff3cd'}}>
                            <h5 className="mb-0">Risk Evaluation</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <Card className="h-100">
                                        <Card.Header>
                                            <strong>Admission Prediction</strong>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="mb-2">
                                                <strong>Predicted Probability:</strong> {(evaluation.risk_evaluation.admit_prediction_vs_actual.capacity_predicted_probability * 100).toFixed(1)}%
                                            </div>
                                            <div className="mb-2">
                                                <strong>Risk Level:</strong> {getRiskLevelBadge(evaluation.risk_evaluation.admit_prediction_vs_actual.capacity_risk_level)}
                                            </div>
                                            <div className="mb-2">
                                                <strong>Actual Outcome:</strong> {evaluation.risk_evaluation.admit_prediction_vs_actual.actual_outcome}
                                            </div>
                                            <div className="mb-3">
                                                <strong>Prediction Accurate:</strong> {getAccuracyBadge(evaluation.risk_evaluation.admit_prediction_vs_actual.prediction_accurate)}
                                            </div>
                                            {evaluation.risk_evaluation.admit_prediction_vs_actual.missed_features.length > 0 && (
                                                <div className="mb-3">
                                                    <strong>Missed Features:</strong>
                                                    <ul className="mt-2">
                                                        {evaluation.risk_evaluation.admit_prediction_vs_actual.missed_features.map((feature, idx) => (
                                                            <li key={idx}>{feature}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            <Card bg="light">
                                                <Card.Body>
                                                    <small><strong>Recommendation:</strong> {evaluation.risk_evaluation.admit_prediction_vs_actual.improvement_recommendation}</small>
                                                </Card.Body>
                                            </Card>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={6}>
                                    <Card className="h-100">
                                        <Card.Header>
                                            <strong>ICU Bounce-Up (24h) Prediction</strong>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="mb-2">
                                                <strong>Predicted Probability:</strong> {(evaluation.risk_evaluation.icu_bounceup_24h_vs_actual.capacity_predicted_probability * 100).toFixed(2)}%
                                            </div>
                                            <div className="mb-2">
                                                <strong>Risk Level:</strong> {getRiskLevelBadge(evaluation.risk_evaluation.icu_bounceup_24h_vs_actual.capacity_risk_level)}
                                            </div>
                                            <div className="mb-2">
                                                <strong>Actual Outcome:</strong> {evaluation.risk_evaluation.icu_bounceup_24h_vs_actual.actual_outcome}
                                            </div>
                                            <div className="mb-3">
                                                <strong>Prediction Accurate:</strong> {getAccuracyBadge(evaluation.risk_evaluation.icu_bounceup_24h_vs_actual.prediction_accurate)}
                                            </div>
                                            {evaluation.risk_evaluation.icu_bounceup_24h_vs_actual.missed_features.length > 0 && (
                                                <div className="mb-3">
                                                    <strong>Missed Features:</strong>
                                                    <ul className="mt-2">
                                                        {evaluation.risk_evaluation.icu_bounceup_24h_vs_actual.missed_features.map((feature, idx) => (
                                                            <li key={idx}>{feature}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            <Card bg="light">
                                                <Card.Body>
                                                    <small><strong>Recommendation:</strong> {evaluation.risk_evaluation.icu_bounceup_24h_vs_actual.improvement_recommendation}</small>
                                                </Card.Body>
                                            </Card>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Card bg="light" className="border-start border-info border-4">
                                        <Card.Body>
                                            <strong>Overall Risk Assessment:</strong>
                                            <p className="mt-2 mb-0">{evaluation.risk_evaluation.risk_overall_assessment}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Patient Visit Summary Evaluation */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header style={{backgroundColor: '#e7e9fc'}}>
                            <h5 className="mb-0">Patient Visit Summary Evaluation</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>Patient Summary Accuracy:</strong> {getAccuracyBadge(evaluation.patient_visit_summary_evaluation.patient_summary_accurate)}
                                    <p className="mt-2">{evaluation.patient_visit_summary_evaluation.patient_summary_assessment}</p>
                                </Col>
                                <Col md={6}>
                                    <strong>Visit Presentation Accuracy:</strong> {getAccuracyBadge(evaluation.patient_visit_summary_evaluation.visit_presentation_accurate)}
                                    <p className="mt-2">{evaluation.patient_visit_summary_evaluation.visit_presentation_assessment}</p>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Card bg="light">
                                        <Card.Body>
                                            <strong>Improvement Recommendation:</strong>
                                            <p className="mt-2 mb-0">{evaluation.patient_visit_summary_evaluation.summary_improvement_recommendation}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Charting Evaluation */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header style={{backgroundColor: '#f8d7da'}}>
                            <h5 className="mb-0">Charting Evaluation</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <strong>Workup Summary:</strong> {getAccuracyBadge(evaluation.charting_evaluation.workup_summary_accurate)}
                                    <p className="mt-2"><small>{evaluation.charting_evaluation.workup_assessment}</small></p>
                                </Col>
                                <Col md={4}>
                                    <strong>Management Summary:</strong> {getAccuracyBadge(evaluation.charting_evaluation.management_summary_accurate)}
                                    <p className="mt-2"><small>{evaluation.charting_evaluation.management_assessment}</small></p>
                                </Col>
                                <Col md={4}>
                                    <strong>Disposition Reasoning:</strong> {getAccuracyBadge(evaluation.charting_evaluation.disposition_reasoning_aligned)}
                                    <p className="mt-2"><small>{evaluation.charting_evaluation.disposition_assessment}</small></p>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Card bg="light">
                                        <Card.Body>
                                            <strong>Improvement Recommendation:</strong>
                                            <p className="mt-2 mb-0">{evaluation.charting_evaluation.charting_improvement_recommendation}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Lessons Learned */}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header style={{backgroundColor: '#f3e8ff'}}>
                            <h5 className="mb-0">Lessons Learned</h5>
                        </Card.Header>
                        <Card.Body>
                            {evaluation.lessons_learned.map((lesson, idx) => (
                                <Card key={idx} bg="light" className={idx < evaluation.lessons_learned.length - 1 ? 'mb-3' : ''}>
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <Badge bg="secondary">{lesson.category}</Badge>
                                            <div>
                                                <small className="text-muted me-2">Generalizability:</small>
                                                {getGeneralizabilityBadge(lesson.generalizability)}
                                            </div>
                                        </div>
                                        <p className="mb-0">{lesson.lesson}</p>
                                    </Card.Body>
                                </Card>
                            ))}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};
